// ─── generators/jaaroverzicht.js ──────────────────────────────────────────────────
// Rapportgenerator: genJaaroverzicht.
// Wijzig hier bij aanpassingen aan dit specifieke rapporttype.

import { MAANDEN, MAANDEN_K, fmtE, fmtD, fmtKg, fmtK, diff, statusLabel, statusBadge, isOnderweg } from '../helpers.js';
import { header, kpiBlok, pageCss, sectTitel, sectTitelKlein, footer, wrapMetHerhalendeHeader } from '../layout.js';
import { balkGrafiek, balkGrafiek3D, lijnGrafiek, taartGrafiek, dubbeleGrafiek, hBalk, renderRanking, renderGrafiek } from '../charts.js';
import { verwerkData } from '../data.js';

function genJaaroverzicht(aanvragen, bedrijfsnaam, cfg, logo) {
  const jaar  = cfg.jaar || new Date().getFullYear();
  const vJaar = jaar - 1;
  const heeftVergelijk = cfg.inclVergelijk !== false;
  const gt  = cfg.grafiekType || 'balk3d';
  const gtB = cfg.grafiekTypeBestemming || 'hbalk';
  const gtR = cfg.grafiekTypeRoutes     || 'hbalk';
  const gtG = cfg.grafiekTypeGoed       || 'taart';

  const d  = verwerkData(aanvragen, jaar, heeftVergelijk ? vJaar : null);
  const dV = heeftVergelijk ? verwerkData(aanvragen, vJaar, null) : null;

  const _css = '<style>' + pageCss(cfg.orientatie) + '</style>';
  const _hdr = header(`Jaaroverzicht ${jaar}`,
    `${d.lijst.length} zendingen${heeftVergelijk&&dV?` · Vergeleken met ${vJaar}`:''}`,
    bedrijfsnaam, logo);
  let body = '';

  // ── KPI vergelijkingstabel ──
  if(cfg.inclKpi !== false) {
    if(heeftVergelijk && dV) {
      const kpis = [
        {lbl:'Totale kosten',      huid:fmtE(d.totK),    vor:fmtE(dV.totK),    delta:diff(d.totK,dV.totK),    k:'#3b82f6'},
        {lbl:'Zendingen',          huid:d.lijst.length,   vor:dV.lijst.length,  delta:diff(d.lijst.length,dV.lijst.length), k:'#1a364c'},
        {lbl:'Gem. per zending',   huid:d.gemK>0?fmtE(d.gemK):'—', vor:dV.gemK>0?fmtE(dV.gemK):'—', delta:d.gemK>0&&dV.gemK>0?diff(d.gemK,dV.gemK):'', k:'#d4a848'},
        {lbl:'Totaal gewicht',     huid:d.totGew>0?fmtKg(d.totGew):'—', vor:dV.totGew>0?fmtKg(dV.totGew):'—', delta:'', k:'#8b5cf6'},
        {lbl:'Gem. doorlooptijd',  huid:d.gemDoorloop?`${d.gemDoorloop} dgn`:'—', vor:dV.gemDoorloop?`${dV.gemDoorloop} dgn`:'—', delta:'', k:'#10b981'},
      ];
      body += `<div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:10px;">
        <table style="width:100%;border-collapse:collapse;font-size:9px;">
          <thead><tr style="background:#1a364c;">
            <th style="padding:7px 10px;text-align:left;color:rgba(255,255,255,.6);font-size:7.5px;text-transform:uppercase;letter-spacing:.06em;font-weight:600;">Indicator</th>
            <th style="padding:7px 10px;text-align:right;color:white;font-size:9px;font-weight:700;">${jaar}</th>
            <th style="padding:7px 10px;text-align:right;color:rgba(255,255,255,.7);font-size:9px;font-weight:600;">${vJaar}</th>
            <th style="padding:7px 10px;text-align:center;color:rgba(255,255,255,.6);font-size:7.5px;text-transform:uppercase;letter-spacing:.06em;font-weight:600;">Δ Verschil</th>
          </tr></thead>
          <tbody>
            ${kpis.map((k,i)=>`
              <tr style="border-bottom:1px solid #f1f5f9;background:${i%2===0?'white':'#fafafa'};">
                <td style="padding:6px 10px;font-weight:600;color:#1a364c;">
                  <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${k.k};margin-right:6px;vertical-align:middle;"></span>${k.lbl}
                </td>
                <td style="padding:6px 10px;text-align:right;font-weight:700;color:#1a364c;font-variant-numeric:tabular-nums;">${k.huid}</td>
                <td style="padding:6px 10px;text-align:right;color:#64748b;font-variant-numeric:tabular-nums;">${k.vor}</td>
                <td style="padding:6px 10px;text-align:center;">${k.delta}</td>
              </tr>`).join('')}
          </tbody>
        </table>
      </div>`;
    } else {
      body += kpiBlok([
        {lbl:'Totale kosten', val:fmtE(d.totK), sub:`${d.lijst.length} zendingen`, kleur:'#3b82f6'},
        {lbl:'Gem. per zending', val:d.gemK>0?fmtE(d.gemK):'—', sub:'Per transport', kleur:'#d4a848'},
        {lbl:'Totaal gewicht', val:d.totGew>0?fmtKg(d.totGew):'—', sub:d.totColli>0?`${d.totColli} colli`:'', kleur:'#8b5cf6'},
        {lbl:'Gem. doorlooptijd', val:d.gemDoorloop?`${d.gemDoorloop} dgn`:'—', sub:'Laad → Los', kleur:'#10b981'},
      ]);
    }
  }

  // ── Kosten per maand grafiek ──
  if(cfg.inclGrafiek !== false) {
    const gtLabel = {balk3d:'3D Balkgrafiek',balk:'Balkgrafiek',lijn:'Lijngrafiek',taart:'Taartgrafiek',donut:'Donut'}[gt]||gt;
    body += `<div class="blok" style="border:1px solid #e2e8f0;border-top:3px solid #3b82f6;border-radius:8px;padding:10px 12px;margin-bottom:10px;page-break-inside:avoid;break-inside:avoid;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <div style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;">Kosten per maand — ${gtLabel}</div>
        ${heeftVergelijk&&dV?`<div style="display:flex;gap:10px;font-size:8px;">
          <span><span style="display:inline-block;width:8px;height:8px;background:#1a364c;border-radius:1px;margin-right:3px;"></span>${jaar}</span>
          <span><span style="display:inline-block;width:8px;height:8px;background:#c2e6ef;border-radius:1px;margin-right:3px;"></span>${vJaar}</span>
        </div>`:''}
      </div>
      ${(gt==='taart'||gt==='donut')
        ? (() => { const nzD=[],nzL=[]; d.kostenM.forEach((v,i)=>{if(v>0){nzD.push(v);nzL.push(MAANDEN_K[i]);}}); return taartGrafiek(nzD,nzL,null,gt==='donut',fmtE,130); })()
        : heeftVergelijk && dV
          ? dubbeleGrafiek(gt, d.kostenM, dV.kostenM, MAANDEN_K, '#1a364c', '#c2e6ef', 120)
          : renderGrafiek(gt, d.kostenM, MAANDEN_K, '#1a364c', 120)
      }
    </div>`;
  }

  // ── Maandtabel ──
  if(cfg.inclMaand !== false) {
    body += `<div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:10px;page-break-inside:avoid;break-inside:avoid;">
      <table style="width:100%;border-collapse:collapse;font-size:8.5px;">
        <thead class="herhaal-kop"><tr style="background:#f7f6f2;">
          <th style="padding:5px 8px;text-align:left;font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.05em;border-bottom:1px solid #e8edf3;">Maand</th>
          <th style="padding:5px 8px;text-align:right;font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;border-bottom:1px solid #e8edf3;">Zend. ${jaar}</th>
          <th style="padding:5px 8px;text-align:right;font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;border-bottom:1px solid #e8edf3;">Gew. ${jaar}</th>
          <th style="padding:5px 8px;text-align:right;font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;border-bottom:1px solid #e8edf3;">Kosten ${jaar}</th>
          ${heeftVergelijk&&dV?`
          <th style="padding:5px 8px;text-align:right;font-size:7.5px;font-weight:700;color:#94a3b8;text-transform:uppercase;border-bottom:1px solid #e8edf3;">Zend. ${vJaar}</th>
          <th style="padding:5px 8px;text-align:right;font-size:7.5px;font-weight:700;color:#94a3b8;text-transform:uppercase;border-bottom:1px solid #e8edf3;">Kosten ${vJaar}</th>
          <th style="padding:5px 8px;text-align:center;font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;border-bottom:1px solid #e8edf3;">Δ</th>`:''}
        </tr></thead>
        <tbody>
          ${MAANDEN.map((m,i)=>`
            <tr style="border-bottom:1px solid #f5f5f5;background:${i%2===0?'white':'#fafafa'};">
              <td style="padding:4px 8px;font-weight:500;">${m.slice(0,3)}</td>
              <td style="padding:4px 8px;text-align:right;">${d.volM[i]||'—'}</td>
              <td style="padding:4px 8px;text-align:right;font-variant-numeric:tabular-nums;">${d.gewM[i]>0?fmtKg(d.gewM[i]):'—'}</td>
              <td style="padding:4px 8px;text-align:right;font-weight:${d.kostenM[i]>0?600:400};font-variant-numeric:tabular-nums;">${d.kostenM[i]>0?fmtE(d.kostenM[i]):'—'}</td>
              ${heeftVergelijk&&dV?`
              <td style="padding:4px 8px;text-align:right;color:#94a3b8;">${dV.volM[i]||'—'}</td>
              <td style="padding:4px 8px;text-align:right;color:#94a3b8;font-variant-numeric:tabular-nums;">${dV.kostenM[i]>0?fmtE(dV.kostenM[i]):'—'}</td>
              <td style="padding:4px 8px;text-align:center;">${diff(d.kostenM[i],dV.kostenM[i])}</td>`:''}
            </tr>`).join('')}
        </tbody>
        <tfoot><tr style="border-top:2px solid #1a364c;background:#f7f6f2;">
          <td style="padding:5px 8px;font-weight:700;">Totaal</td>
          <td style="padding:5px 8px;text-align:right;font-weight:700;">${d.lijst.length}</td>
          <td style="padding:5px 8px;text-align:right;font-weight:700;font-variant-numeric:tabular-nums;">${d.totGew>0?fmtKg(d.totGew):'—'}</td>
          <td style="padding:5px 8px;text-align:right;font-weight:700;color:#1a364c;font-variant-numeric:tabular-nums;">${fmtE(d.totK)}</td>
          ${heeftVergelijk&&dV?`
          <td style="padding:5px 8px;text-align:right;font-weight:700;color:#94a3b8;">${dV.lijst.length}</td>
          <td style="padding:5px 8px;text-align:right;font-weight:700;color:#94a3b8;font-variant-numeric:tabular-nums;">${fmtE(dV.totK)}</td>
          <td style="padding:5px 8px;text-align:center;font-weight:700;">${diff(d.totK,dV.totK)}</td>`:''}
        </tr></tfoot>
      </table>
    </div>`;
  }

  // ── Bestemmingen + routes ──
  if(cfg.inclBestemming !== false && (d.topLanden.length > 0 || d.topRoutes.length > 0)) {
    const cols = d.topLanden.length > 0 && d.topRoutes.length > 0 ? '1fr 1fr' : '1fr';
    body += `<div style="display:grid;grid-template-columns:${cols};gap:10px;margin-bottom:10px;">`;
    if(d.topLanden.length>0) body += `<div class="blok" style="border:1px solid #e2e8f0;border-top:3px solid #d4a848;border-radius:8px;padding:10px 12px;page-break-inside:avoid;break-inside:avoid;">
      <div style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;">Top bestemmingen</div>
      ${renderRanking(d.topLanden,n=>n+'x',gtB,130)}
    </div>`;
    if(d.topRoutes.length>0) body += `<div class="blok" style="border:1px solid #e2e8f0;border-top:3px solid #10b981;border-radius:8px;padding:10px 12px;page-break-inside:avoid;break-inside:avoid;">
      <div style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;">Meest gebruikte routes</div>
      ${renderRanking(d.topRoutes,n=>n+'x',gtR,130)}
    </div>`;
    body += `</div>`;
  }

  // ── Goederensoorten ──
  if(cfg.inclGoed !== false && d.topGoed.length > 0) {
    body += `<div class="blok" style="border:1px solid #e2e8f0;border-top:3px solid #8b5cf6;border-radius:8px;padding:10px 12px;margin-bottom:10px;page-break-inside:avoid;break-inside:avoid;">
      <div style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;">Goederensoorten</div>
      ${renderRanking(d.topGoed,n=>n+'x',gtG,130)}
    </div>`;
  }

  return wrapMetHerhalendeHeader(_css, _hdr, body);
}

export { genJaaroverzicht };
